import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { CheckoutTableProps } from './CheckoutTable.types';
import { CheckoutTableStyles as styles } from './CheckoutTable.styles';
import { Text, TouchableOpacity, View } from 'react-native';
import CartItem from '../CartItem';
import { Button } from '../../../../../components/Button';
import { STYLE_VARS } from '../../../../../constants/style-vars';
import { useIsDesktop } from '../../../../../hooks/useIsDeskTop';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../services/redux';
import { IProduct } from '../../../../../models/IProduct';
import { convertHKPrice } from '../../../../../services/utils';
import {
  addToCart,
  deleteItem,
  updateCart,
} from '../../../../../services/redux/reducers/CartSlice';
import { recommentItem } from '../../CheckoutPage.constants';
import { COLORS, FONTS } from '../../../../../constants';
import { Image } from '../../../../../components/Image';
import { Customer, Product } from '../../../../../types/woocommerce.types';
import { wooCommerceApi } from '../../../../../services/api/wooCommerceApi';
import { Loading } from '../../../../../components';
import Dialog from '../../../../../components/Dialog';
import RenderHTML from 'react-native-render-html';
import { NavigationService } from '../../../../../services/navigation';
import { Alert } from '../../../../../services/alert';

const CheckoutTable: FC<CheckoutTableProps> = ({}) => {
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();
  const { items } = useSelector((redux: RootState) => redux.cartReducer);
  const { info } = useSelector((redux: RootState) => redux.authReducer);
  const [count, setCount] = useState<number>(1);
  const [customer, setCustomer] = useState<Customer>();
  const [isPlacingOrder, setIsPlacingOrder] = useState<boolean>(false);
  const [recommendProduct, setRecommendProduct] = useState<Product>();
  const [isChangedCart, setIsChangedCart] = useState<boolean>(false);

  const [cartData, setCartData] = useState<IProduct[]>(items);
  const renderHeaderContent = useMemo(() => {
    const contents = isDesktop()
      ? [
          {
            title: 'Type',
            width: '20%',
          },
          {
            title: 'Products',
            width: '30%',
          },
          {
            title: 'Price',
            width: '15%',
          },
          {
            title: 'Subtotal',
            width: '20%',
          },
          {
            title: 'Subtotal(HK$)',
            width: '15%',
          },
        ]
      : [
          {
            title: 'Products',
            width: '50%',
          },
          {
            title: 'Price',
            width: '25%',
          },
          {
            title: 'Subtotal',
            width: '25%',
          },
        ];

    return contents.map((content, index) => (
      <View
        style={{
          width: content.width,
          alignItems: isDesktop() || index === 0 ? 'flex-start' : 'center',
          paddingLeft: index === 0 ? (isDesktop() ? 40 : 20) : 0,
        }}
        key={content.title}
      >
        <Text style={styles.th_text}>{content.title}</Text>
      </View>
    ));
  }, [isDesktop]);

  const getCustomer = useCallback(async () => {
    try {
      const res = await wooCommerceApi.get(`customers/${info?.ID}`);
      setCustomer(res.data);
    } catch (error: any) {
      console.log(error);
    } finally {
    }
  }, [info?.ID]);

  const getRecommendProduct = useCallback(async () => {
    try {
      const res = await wooCommerceApi.get(`products`, {
        orderby: 'popularity',
      });
      setRecommendProduct(res.data[0]);
    } catch (error: any) {
      console.log(error);
    } finally {
    }
  }, []);

  const handlePlaceOrder = useCallback(async () => {
    let lineItems: { product_id: number; quantity: number }[] = [];
    items.map((item) => {
      lineItems.push({ product_id: item.id, quantity: item.amount });
    });

    if (lineItems.length > 0 && info) {
      setIsPlacingOrder(true);
      wooCommerceApi
        .post('orders', {
          billing: {
            email: info?.user_email,
            first_name: info?.first_name,
            last_name: info?.last_name,
            customer_id: info?.ID,
          },
          line_items: lineItems,
          customer_id: info?.ID,
        })
        .then((response: any) => {
          setCartData([]);
          dispatch(updateCart([]));
          Alert.alert(
            'Order received',
            'Thank You. Your order has been received. We will notify you when its ready for pick-up.',
            [
              {
                text: 'OK',
                onPress: () => {},
              },
            ]
          );
        })
        .catch((error: any) => {
          console.log(error);
        })
        .finally(() => {
          setIsPlacingOrder(false);
        });
    }
  }, [items, dispatch, info]);

  useEffect(() => {
    getCustomer();
    getRecommendProduct();
  }, [getCustomer, getRecommendProduct]);

  useEffect(() => {
    setCartData(items);
  }, [items]);

  const handleChangeAmount = useCallback(
    (id: number, type: 'increase' | 'decrease') => {
      setIsChangedCart(true);
      const newCartData: IProduct[] = cartData.map((data) => {
        if (data.id === id) {
          return {
            ...data,
            amount: data.amount
              ? type === 'increase'
                ? data.amount + 1
                : data.amount - 1
              : 1,
          };
        }
        return data;
      });
      setCartData(newCartData);
    },
    [cartData]
  );

  const handleDeleteCardItem = useCallback(
    (id: number) => {
      setIsChangedCart(true);
      // dispatch(deleteItem(id));
      const newCartData = cartData.filter((data) => data.id !== id);
      setCartData(newCartData);
    },
    [cartData]
  );

  const handleAddCart = useCallback(() => {
    if (recommendProduct) {
      let isAdded = false;

      if (items.filter((e) => e.id === recommendProduct.id).length > 0) {
        isAdded = true;
      }

      if (isAdded) {
        const newCartData: IProduct[] = items.map((data) => {
          if (data.id === recommendProduct.id) {
            return {
              ...data,
              amount: data.amount + count,
            };
          }
          return data;
        });
        dispatch(updateCart(newCartData));
      } else {
        dispatch(
          addToCart({
            id: recommendProduct.id,
            photo: recommendProduct.images[0]?.src ?? '',
            totalAmount: count,
            amount: count,
            price: parseFloat(recommendProduct.price),
            type: recommendProduct.categories[0].name ?? '',
            productName: recommendProduct.name,
          })
        );
      }
      setIsChangedCart(false);
      Alert.alert('Product added to your cart!', '', [
        {
          text: 'Go to Checkout',
          onPress: () => {
            NavigationService.navigate('EShopCheckout');
          },
        },
        {
          text: 'Continue Shopping',
          onPress: () => {},
        },
      ]);
    }
  }, [recommendProduct, count, items, dispatch]);

  const renderBodyContent = useMemo(() => {
    if (cartData.length === 0) {
      return (
        <View style={styles.noDataWrapper}>
          <Text style={[styles.noDataText, { fontSize: 13 }]}>
            Your shopping cart is currently empty.
          </Text>
        </View>
      );
    }

    if (isDesktop()) {
      return cartData.map((groupItem, index) => (
        <View
          style={{
            flexDirection: 'row',
            marginBottom: 20,
            alignItems: 'center',
            width: '100%',
          }}
          key={index}
        >
          <CartItem
            item={groupItem}
            onDecreaseAmount={(id) => {
              handleChangeAmount(id, 'decrease');
            }}
            onDelete={handleDeleteCardItem}
            onIncreaseAmount={(id) => {
              handleChangeAmount(id, 'increase');
            }}
          />
        </View>
      ));
    }
    return cartData.map((groupItem, index) => (
      <View style={{ marginBottom: 20 }} key={index}>
        <CartItem
          item={groupItem}
          onDecreaseAmount={(id) => {
            handleChangeAmount(id, 'decrease');
          }}
          onDelete={handleDeleteCardItem}
          onIncreaseAmount={(id) => {
            handleChangeAmount(id, 'increase');
          }}
        />
      </View>
    ));
  }, [cartData, isDesktop, handleChangeAmount, handleDeleteCardItem]);

  const cartTotal = useMemo(() => {
    let total: number = 0;
    for (let i = 0; i < items.length; i++) {
      const data = items[i];
      if (data.amount) {
        total += data.amount * data.price;
      }
    }
    return total;
  }, [items]);

  const handleUpdateCart = () => {
    setIsChangedCart(false);
    dispatch(updateCart(cartData));
  };

  return (
    <>
      <View>
        <View style={styles.thead}>{renderHeaderContent}</View>
        <View>{renderBodyContent}</View>
      </View>
      <View style={styles.container}>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            borderTopWidth: 0.5,
            borderTopColor: STYLE_VARS.fccDarkGrey,
            paddingVertical: 15,
          }}
        />
      </View>
      <View
        style={{
          flexDirection: isDesktop() ? 'row-reverse' : 'column',
          justifyContent: isDesktop() ? 'space-between' : undefined,
          width: '100%',
        }}
      >
        <View
          style={{ paddingHorizontal: 20, width: isDesktop() ? '55%' : '100%' }}
        >
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginBottom: 20,
              paddingHorizontal: isDesktop() ? 15 : 0,
              paddingVertical: isDesktop() ? 10 : 0,
              backgroundColor: isDesktop() ? COLORS.LIGHT_GREY : undefined,
            }}
          >
            <Text style={styles.totalText}>CART TOTAL</Text>
            <Text style={styles.totalText}>{convertHKPrice(cartTotal)}</Text>
          </View>
          <View style={{ flexDirection: 'column', marginBottom: 30 }}>
            <Text style={styles.title}>PAYMENT TERMS</Text>
            <Text style={styles.content}>
              Orders will be processed at a minimum period of 48 hours of your
              order time. We will notify you when your order is packed and ready
              for pick up. Your member account will be charged once you have
              picked up your order at the Kowloon Cricket Club. You may cancel
              your order if it has not yet been processed. For more details,
              please read the KCC website terms and conditions.
            </Text>
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
              {isChangedCart ? (
                <Button
                  text="UPDATE CART"
                  style={STYLE_VARS.blueButton}
                  onPress={handleUpdateCart}
                />
              ) : (
                <Button
                  text="PLACE ORDER"
                  style={STYLE_VARS.blueButton}
                  onPress={() => handlePlaceOrder()}
                />
              )}
            </View>
          </View>
        </View>
        <View
          style={{ paddingHorizontal: 20, width: isDesktop() ? '42%' : '100%' }}
        >
          <Text style={styles.title}>YOU MAY INTERESTED IN...</Text>
          {recommendProduct && (
            <View style={styles.productRecomentWrapper}>
              <Image
                source={{ uri: recommendProduct?.images[0].src }}
                style={{
                  width: '100%',
                  height: isDesktop() ? 200 : 300,
                  marginBottom: 20,
                  resizeMode: 'contain',
                }}
              />
              <Text style={styles.title}>{recommendProduct?.name}</Text>
              <Text
                style={{
                  fontFamily: FONTS.AvenirMedium,
                  fontWeight: '400',
                  color: STYLE_VARS.fccDarkerGrey,
                  marginBottom: 20,
                }}
              >
                <RenderHTML
                  source={{ html: recommendProduct?.short_description }}
                  tagsStyles={{
                    p: {
                      marginTop: 0,
                      fontFamily: FONTS.AvenirMedium,
                      fontWeight: '400',
                      color: STYLE_VARS.fccDarkerGrey,
                      fontSize: 13,
                      textTransform: 'uppercase',
                    },
                  }}
                />
              </Text>
              <View style={styles.countWrapper}>
                <View style={styles.count}>
                  <TouchableOpacity
                    activeOpacity={1}
                    style={styles.countBtn}
                    onPress={() => {
                      if (count > 1) {
                        setCount(count - 1);
                      }
                    }}
                  >
                    <Text style={styles.countBtnText}>-</Text>
                  </TouchableOpacity>
                  <Text style={styles.countText}>{count}</Text>
                  <TouchableOpacity
                    activeOpacity={1}
                    style={styles.countBtn}
                    onPress={() => setCount(count + 1)}
                  >
                    <Text style={styles.countBtnText}>+</Text>
                  </TouchableOpacity>
                </View>
              </View>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <View style={{ width: '47%' }}>
                  <Button
                    style={[STYLE_VARS.unActiveButton, { width: '100%' }]}
                    className=""
                    text="VIEW MORE"
                    arrowStyle={{ tintColor: STYLE_VARS.primaryColor }}
                    textStyle={{ color: STYLE_VARS.textColor }}
                    onPress={() => {
                      NavigationService.push('EShopDetailPage', {
                        id: recommendProduct.id,
                      });
                    }}
                  />
                </View>
                <View style={{ width: '47%' }}>
                  <Button
                    style={[STYLE_VARS.fullWGreenButton, { width: '100%' }]}
                    text="ADD TO CART"
                    onPress={() => handleAddCart()}
                  />
                </View>
              </View>
            </View>
          )}
        </View>
      </View>
      <View
        style={{
          paddingHorizontal: 20,
          paddingVertical: 30,
        }}
      >
        <Text style={styles.noteText}>
          ALCOHOL WILL NOT BE SOLD OR SUPPLIED TO A MINOR (UNDER 18).
        </Text>
      </View>
      {isPlacingOrder && <Loading isFullScreen={true} />}
    </>
  );
};

export default CheckoutTable;
